import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, MessageSquareText, Plus, StickyNote } from 'lucide-react'

import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Textarea } from '@components/ui/textarea'
import { useActivePatient } from '@context/ActivePatientContext'
import { useToast } from '@hooks/use-toast'
import { api, getApiErrorMessage } from '@services/api'
import { cn } from '@lib/utils'
import type { AxiosError } from 'axios'
import type { Note, NoteType } from '@/types'

/**
 * Notas de recepción — lo que el paciente contó por WhatsApp (Valentina) o en
 * mostrador: motivo, tratamiento de interés, preferencias, pendientes de
 * seguimiento. Viven en /notes (HMS clinical_notes) con note_type FOLLOW_UP u
 * OTHER; las notas clínicas firmadas siguen en Evolución / Historia clínica.
 */

interface NoteList {
  items: Note[]
  total: number
}

const TYPE_LABEL: Record<NoteType, string> = {
  FOLLOW_UP: 'Seguimiento',
  OTHER: 'Recepción',
  CLINICAL: 'Clínica',
  PROCEDURE: 'Procedimiento',
  REFERRAL: 'Referencia',
  LAB: 'Laboratorio',
}

const TYPE_STYLE: Record<NoteType, string> = {
  FOLLOW_UP: 'bg-amber-50 text-amber-800 border-amber-200',
  OTHER: 'bg-slate-100 text-slate-700 border-slate-200',
  CLINICAL: 'bg-teal-50 text-teal-800 border-teal-200',
  PROCEDURE: 'bg-sky-50 text-sky-800 border-sky-200',
  REFERRAL: 'bg-violet-50 text-violet-800 border-violet-200',
  LAB: 'bg-rose-50 text-rose-800 border-rose-200',
}

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString('es-MX', {
    timeZone: 'America/Mexico_City',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

const defaultTitle = () =>
  `Nota de recepción · ${new Date().toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City', day: 'numeric', month: 'short' })}`

export function NotasRecepcionTab() {
  const { activePatientId } = useActivePatient()
  const { toast } = useToast()

  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [composing, setComposing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState(defaultTitle)
  const [content, setContent] = useState('')
  const [noteType, setNoteType] = useState<NoteType>('FOLLOW_UP')

  const load = useCallback(async () => {
    if (!activePatientId) return
    setLoading(true)
    setError(null)
    try {
      const data = await api.get<NoteList>(`/notes/patient/${activePatientId}`, { params: { limit: 100 } })
      setNotes([...(data.items ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at)))
    } catch (err) {
      setError(getApiErrorMessage(err as AxiosError, 'No se pudieron cargar las notas'))
    } finally {
      setLoading(false)
    }
  }, [activePatientId])

  useEffect(() => {
    void load()
  }, [load])

  const grouped = useMemo(() => {
    const byDay = new Map<string, Note[]>()
    for (const n of notes) {
      const day = new Date(n.created_at).toLocaleDateString('es-MX', { timeZone: 'America/Mexico_City', weekday: 'long', day: 'numeric', month: 'long' })
      byDay.set(day, [...(byDay.get(day) ?? []), n])
    }
    return Array.from(byDay.entries())
  }, [notes])

  const save = async () => {
    if (!activePatientId || !content.trim()) return
    setSaving(true)
    try {
      await api.post<Note>('/notes', {
        patient_id: activePatientId,
        title: title.trim() || defaultTitle(),
        content: content.trim(),
        note_type: noteType,
      })
      toast({ title: 'Nota guardada', description: 'Ya aparece en el expediente del paciente.' })
      setContent('')
      setTitle(defaultTitle())
      setComposing(false)
      await load()
    } catch (err) {
      toast({ title: 'No se pudo guardar', description: getApiErrorMessage(err as AxiosError, 'Intenta de nuevo'), variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  if (!activePatientId) return null

  return (
    <div className="flex flex-col gap-4 sm:gap-6 max-w-3xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-serif text-xl sm:text-2xl text-ml-primary flex items-center gap-2">
            <MessageSquareText className="h-5 w-5 shrink-0" />
            Notas de recepción
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Lo que el paciente contó por WhatsApp o en mostrador: motivo, tratamiento que le interesa, preferencias y pendientes. Las escribe el equipo y también Valentina.
          </p>
        </div>
        {!composing && (
          <Button size="sm" className="w-full sm:w-auto" onClick={() => setComposing(true)}>
            <Plus className="h-4 w-4 mr-1" /> Nueva nota
          </Button>
        )}
      </div>

      {composing && (
        <div className="rounded-xl border bg-card p-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
            <div className="flex flex-col gap-1.5 min-w-0">
              <Label htmlFor="note-title">Título</Label>
              <Input id="note-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Tipo</Label>
              <div className="inline-flex rounded-md border overflow-hidden">
                {(['FOLLOW_UP', 'OTHER'] as NoteType[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={cn('px-3 py-2 text-xs', noteType === t ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground')}
                    onClick={() => setNoteType(t)}
                  >
                    {TYPE_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="note-content">Nota</Label>
            <Textarea
              id="note-content"
              rows={5}
              placeholder="Ej. Refiere sensibilidad en molar inferior derecho. Le interesa limpieza + valoración. Prefiere tardes. Pendiente: confirmar el lunes."
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setComposing(false); setContent('') }} disabled={saving}>Cancelar</Button>
            <Button size="sm" onClick={save} disabled={saving || !content.trim()}>
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Guardar nota
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando notas…
        </div>
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>
      ) : notes.length === 0 ? (
        <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
          <StickyNote className="h-6 w-6 opacity-60" />
          Todavía no hay notas de recepción para este paciente.
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {grouped.map(([day, items]) => (
            <section key={day} className="flex flex-col gap-2">
              <h3 className="text-xs uppercase tracking-wide text-muted-foreground">{day}</h3>
              {items.map((n) => (
                <article key={n.id} className="rounded-xl border bg-card p-4 flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn('text-[11px] font-medium px-2 py-0.5 rounded-full border', TYPE_STYLE[n.note_type] ?? TYPE_STYLE.OTHER)}>
                      {TYPE_LABEL[n.note_type] ?? n.note_type}
                    </span>
                    <span className="font-medium text-sm min-w-0 break-words">{n.title}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{n.content}</p>
                  <p className="text-xs text-muted-foreground">
                    {n.author_name} · {formatWhen(n.created_at)}
                  </p>
                </article>
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
